// Single source of truth for ALL editable site content — text, images, video.
//
// The public site reads from here via useContent(); the /admin backpanel
// writes to here. Persisted to localStorage so it survives reloads with no
// backend. Media (logo/photos/video) are stored as data-URL strings so they
// live in localStorage too — no server upload needed.
//
// Shape: a single nested object. Defaults below mirror exactly what ships in
// the code today, so an un-edited site looks identical.

import { useEffect, useState } from "react";
import { IMG } from "./placeholders";

// v5: homepage rebuilt (video hero + about/stats/projects/testimonials/FAQ/CTA),
// site locked to the light theme, socials expanded. The key bump is deliberate:
// a stale v4 blob in a visitor's localStorage would deep-merge OVER all the new
// defaults below and shadow them (this is exactly what made earlier phone-number
// and Instagram changes appear not to deploy). Bumping forces every browser to
// re-seed from these defaults once.
const KEY = "nr_site_content_v37"; // v37: Home page "Who We Are" points rephrased for clarity (esp. point 2); FAQ answers rewritten in plainer, simpler language while keeping the same facts

// ---------------------------------------------------------------------------
// DEFAULTS — current site content
// ---------------------------------------------------------------------------
export const DEFAULT_CONTENT = {
  // Shared business details — used in nav, footer, contact page, home phases.
  settings: {
    brandName: "Nausiddhi Realty",
    brandTagline: "The New Standard",
    // Primary number — enquiries (site visits, plots, general contact). Used
    // site-wide as the default phone/WhatsApp CTA.
    phone: "+91 99212 10076",
    phoneRaw: "+919921210076",
    whatsappRaw: "919921210076",
    // Careers & Admin line — the original number, now scoped to internal /
    // HR contact only. Shown separately in Footer + Contact page, never used
    // as the general enquiry CTA.
    hrPhone: "+91 70090 05400",
    hrPhoneRaw: "+917009005400",
    email: "info@nausiddhirealty.com",
    instagram: "https://www.instagram.com/nausiddhi_realty",
    linkedin: "https://www.linkedin.com/company/nausiddhi/",
    facebook: "https://www.facebook.com/share/17XrNQgz8u/",
    // Logos (paths or data URLs). Defaults point at the bundled files.
    // logoMark = header/nav logo — using the client-supplied
    // "Nausiddhi Header Logo.png" (copied to public/brand/header-logo.png
    // for a clean, space-free path).
    logoMark: "/brand/header-logo.png",
    logoMedium: "/brand/logo-full.png",
    office: {
      // Exact registered office address (client-supplied, verbatim source):
      // "Third Floor, Kohinoor World Tower, T3, 308-309, Old Mumbai - Pune
      // Hwy, Empire Estate Phase 1, Empire Estate Phase-II, Chinchwad,
      // Pimpri-Chinchwad, Maharashtra 411019"
      line1: "Third Floor, Kohinoor World Tower, T3, 308–309",
      line2: "Old Mumbai – Pune Hwy, Empire Estate Phase I & II",
      line3: "Chinchwad, Pimpri-Chinchwad",
      line4: "Maharashtra 411019",
      hours: "Mon–Sat · 10:00 – 18:00 IST (GMT +5:30)",
      // A text/business-name query on the keyless ?q= embed sometimes just
      // centers the map on the area without dropping an actual pin. Using
      // the exact lat,lng from the real "Nausiddhi Realty" Google Maps
      // listing (resolved from the client's share link, maps.app.goo.gl/
      // rJWYk4b9gXYyAJv89 → .../@18.6345621,73.7997915) forces a pin at the
      // precise verified marker (Tower 3, 11th Floor), matching Google's own
      // listing exactly instead of guessing from the address text.
      mapQuery: "18.6345621,73.7997915",
      coordinates: "18.6345621° N · 73.7997915° E",
      cityLabel: "Pimpri-Chinchwad, Maharashtra",
    },
  },

  // Footer (mostly pulls from settings; brandLine is its own copy).
  footer: {
    brandLine: "Premium plotted projects across Maharashtra - Mangaon, Shrivardhan, Dapoli, and Shirdi.",
    // Closing CTA band across the top of the footer — echoes the About
    // page's closing panel, so the site ends on the same note everywhere.
    ctaTitle: "Ready to invest in your future?",
    ctaLine: "Book a site visit, or talk to our team about the right project for you.",
    ctaButton: "Reserve a Plot",
  },

  // About page. Rewritten from the client-supplied Company Profile PDF
  // (info/Nausiddhi Company Profile.pdf) — trust- and investment-focused
  // rather than project-focused, per client direction.
  about: {
    metaTitle: "About Us - Nausiddhi Realty | Trusted Real Estate Developers in Maharashtra",
    metaDescription: "Nausiddhi Realty - one of Maharashtra's emerging real estate development companies. Decades of combined leadership expertise, legally verified developments, and long-term wealth creation for investors.",
    heroEyebrow: "Who we are",
    heroTitle: "The New",
    heroTitleEm: "Standard.",
    heroSub: "One of Maharashtra's emerging real estate development companies, committed to creating premium plotted developments, integrated townships, resort homes, and investment-driven real estate opportunities.",
    heroLocations: "Mangaon · Raigad, Shrivardhan · Raigad, Dapoli · Ratnagiri, Shirdi · Ahilyanagar",
    heroImage: IMG.aboutHero,
    heroCaption: "Nausiddhi Realty · Maharashtra",

    // ---- WHO WE ARE ----
    // whoPara1 removed: it duplicated heroSub almost verbatim, one screen
    // apart. The eyebrow was also literally the same as the hero's, so this
    // section now leads on what the hero does NOT say (leadership depth,
    // legal rigour) rather than restating it.
    whoEyebrow: "The company",
    whoTitle: "Decades of expertise. One clear standard.",
    whoPara2: "Backed by decades of combined leadership expertise, we deliver transparent, legally verified, value-driven projects built for long-term wealth.",
    whoTags: ["Premium developments", "Transparency", "Long-term value"],
    companyImage: IMG.aboutCompany,

    // Mission list + Core Values chips removed — every one of those eleven
    // items was already stated by the Why Choose Us reasons or the whoTags
    // above. The vision statement moved to the full-bleed image band below
    // (the page previously used the "Our vision" eyebrow twice).

    numbers: [
      { v: "369+", label: "Acres across projects" },
      { v: "5,400+", label: "Total plots" },
      { v: "1,350+", label: "Happy families" },
      { v: "4+", label: "Active projects" },
    ],

    storyImage: IMG.story,
    storyImageCaption: "From Pune, outward",
    storyBadgeLine: "4 active projects across Maharashtra",
    storyEyebrow: "Our story",
    storyTitle: "Built from Pune. Delivered across Maharashtra.",
    storyPara1: "Nausiddhi Realty was founded on a simple conviction - land in the right location, with clear title and finished infrastructure, holds value across generations. From Pimpri-Chinchwad, Pune, we develop plotted projects along Maharashtra's growth corridors.",
    storyPara2: "Today we manage four active projects - Nausiddhi Residence (Mangaon), BlueRain Resort Homes (Shrivardhan), Seaview Enclave (Dapoli) and Sai Shrushti (Shirdi) - spanning 369+ acres and 5,400+ plots, each handed over with clear title and registration in the owner's name.",

    // ---- WHY CHOOSE US ----
    // Reduced from nine reasons to four. The removed five were restatements:
    // "Experienced Leadership" is covered by the Leadership section,
    // "End-to-End Expertise" by the How We Work process, and "Transparent
    // Dealings" / "Regulatory Compliance" / "Value-Driven Developments" each
    // repeat one of the four kept below.
    whyEyebrow: "The difference",
    whyTitle: "Why investors choose Nausiddhi.",
    whyLede: "Four reasons investors and families choose to build their future with us.",
    whyReasons: [
      { n: "01", title: "Legal & Documentation Focus", body: "Complete due diligence and accurate documentation at every step, before you commit." },
      { n: "02", title: "Strategic Locations", body: "Parcels chosen for connectivity, infrastructure and future appreciation." },
      { n: "03", title: "Long-Term Wealth Creation", body: "Locations and developments built to grow in value across generations." },
      { n: "04", title: "Customer-Centric Approach", body: "Transparency, responsiveness and a relationship that lasts past the sale." },
    ],

    // ---- HOW WE WORK (investment-focused process, from "Our Process") ----
    // Reduced from eight steps to six: "Government Approvals" folded into
    // Legal Verification (the buyer experiences them as one assurance), and
    // "Marketing & Sales" dropped as internal-facing — it tells a buyer
    // nothing about what they receive.
    processEyebrow: "How we work",
    processTitle: "A transparent, systematic approach to building your wealth.",
    processLede: "Six disciplined stages - from identifying the land to supporting you long after possession - so every investment is backed by process, not promises.",
    processSteps: [
      { n: "01", title: "Land Identification", body: "Prime parcels selected for growth potential and strategic value." },
      { n: "02", title: "Legal Verification", body: "Thorough due diligence, title verification and every statutory clearance secured." },
      { n: "03", title: "Planning & Design", body: "Layouts designed for optimum utilization and long-term value." },
      { n: "04", title: "Infrastructure Development", body: "Roads, water, power and drainage, built to quality standards." },
      { n: "05", title: "Registration", body: "Smooth documentation, registered in your name, day one." },
      { n: "06", title: "Customer Support", body: "Our team stays available well after possession." },
    ],
    // Optional — a real site-progress photo (compound wall, roads, RERA
    // signage) under the process grid. Empty by default: no stock imagery
    // stands in for it. Upload via /admin -> About -> How we work, once the
    // client supplies one.
    processImage: "",
    processImageCaption: "On-site progress",

    // Our Strength (6 discipline cards) removed entirely — org-chart filler.
    // Every discipline it named was already implied by the process steps
    // above, claimed in Why Choose Us, or (Leadership) stated a third time
    // by the Leadership section below.

    // ---- VISION (full-bleed image band) ----
    // This is now the page's ONLY "Our vision" moment; the former 3-column
    // vision statement was folded in here as visionStatement below, since
    // the page previously used this eyebrow twice.
    visionImage: IMG.vision,
    visionEyebrow: "Our vision",
    visionTitleLine1: "Building tomorrow,",
    visionTitleLine2: "rooted in nature.",
    visionStatement: "To be the most trusted real estate brand, creating thoughtfully planned spaces that enrich lives and build lasting legacies.",
    visionLine: "Every acre is thoughtfully planned to create a lifestyle that blends modern living with timeless natural beauty.",

    // ---- LEADERSHIP (top 3 from the Company Profile PDF) ----
    peopleEyebrow: "Leadership",
    peopleTitle: "Guided by vision. Driven by expertise.",
    peopleLede: "Our strength lies in experienced leadership backed by decades of combined expertise - committed to creating lasting value.",
    people: [
      {
        role: "Chairman",
        name: "Mr. Prathmesh Mairal",
        years: "30+ Years",
        line: "As Chairman of Nausiddhi Realty, he provides strategic direction, drives long-term business growth, and upholds the company's commitment to excellence, innovation, and sustainable development.",
        photo: "/img/Prathamesh.png",
      },
      {
        role: "Managing Director & CEO",
        name: "Mr. Vrushabh Chordiya",
        years: "24+ Years",
        line: "Brings extensive experience in land acquisition, government liaison, statutory compliance, and land use documentation. His visionary leadership ensures legal compliance, operational efficiency, and drives the company's expansion.",
        photo: "/img/Vrushab.png",
      },
    ],

    ctaEyebrow: "Ready when you are",
    ctaTitle: "Let's build the future together.",
    ctaLine: "Book a site visit - or a video walkthrough if you're outside India. We share the title documents, approvals and master plan before you commit to anything.",
    ctaButton: "Plan a visit",
    // ctaImage kept for reference/future reuse — the CTA panel is
    // deliberately photo-free now (see about-cta--panel in about.css).
    ctaImage: IMG.cta,
  },

  // Contact page (contact details themselves live in settings).
  contact: {
    metaTitle: "Contact - Nausiddhi Realty",
    metaDescription: "Speak with the Nausiddhi Realty concierge about a private viewing, plot reservation, or build.",
    heroEyebrow: "Get in touch",
    heroTitle: "Let's talk",
    heroTitleEm: "land.",
    heroImage: IMG.contactHero,
    heroCaption: "Pimpri-Chinchwad · Pune",
    interests: "A private viewing, Plot reservation, Build & architecture, General enquiry",
    nriTitle: "NRI & International Buyers",
    nriLine: "We work with NRI buyers anywhere in the world. Video site walkthroughs, document review over email, and registration support via Power of Attorney - all coordinated on your time zone over WhatsApp.",
  },

  // Channel Partner page (/partners).
  // NOTE: V7Vista is building the real CP Portal (partner logins, lead
  // tracking, commission statements). Until that URL exists, partnerLoginUrl
  // stays empty and the page shows a "coming soon" state instead of a dead
  // login button. Drop the URL in via /admin the day it goes live.
  partners: {
    metaTitle: "Channel Partners - Nausiddhi Realty",
    metaDescription: "Partner with Nausiddhi Realty. Register as a channel partner, submit client leads, and earn on premium plotted developments across Maharashtra.",
    heroEyebrow: "Channel partners",
    heroTitle: "Grow with",
    heroTitleEm: "Nausiddhi.",
    heroSub: "Empanel with one of Maharashtra's fastest-growing plotted-development brands. Transparent payouts, protected lead attribution, and full sales support.",
    heroImage: IMG.projectsHero,
    heroCaption: "Partner programme",

    // Login block
    loginEyebrow: "Already empanelled?",
    loginTitle: "Partner login",
    loginLine: "Track your registered leads, site visits and payout status in the partner portal.",
    loginButton: "Open partner portal",
    partnerLoginUrl: "",   // ← V7Vista CP Portal URL goes here when live
    loginPendingNote: "The partner portal is launching shortly. Register below and we'll send your login details the moment it's live.",

    // Why partner with us
    benefitsEyebrow: "Why partner",
    benefitsTitle: "Built for partners who sell seriously.",
    benefits: [
      { n: "01", title: "Protected Attribution", body: "Every lead you register is tagged to you in our CRM from the moment it's submitted." },
      { n: "02", title: "Transparent Payouts", body: "Clear commission structure, agreed upfront, with payout status visible to you." },
      { n: "03", title: "Ready Inventory", body: "Four active projects, 5,400+ plots, all NA-approved with clear title." },
      { n: "04", title: "Full Sales Support", body: "Site visit coordination, collateral, and our team joining your client meetings." },
    ],

    // Registration form
    registerEyebrow: "Become a partner",
    registerTitle: "Empanel with us.",
    registerLine: "Share your details and our channel-partner desk will get in touch to complete your empanelment.",
    registerButton: "Submit registration",

    // Client-lead form
    leadEyebrow: "Registered partners",
    leadTitle: "Register a client lead.",
    leadLine: "Submit your client's details to lock the lead against your name. You'll receive confirmation from our team.",
    leadButton: "Register this lead",
  },

  // Projects listing page hero (cards come from projectsStore).
  projects: {
    metaTitle: "Projects - Nausiddhi Realty",
    metaDescription: "Four NA-approved plotted projects across Maharashtra - Nausiddhi Residence (Mangaon), BlueRain Resort Homes (Shrivardhan), Seaview Enclave (Dapoli), Sai Shrushti (Shirdi). 5,400+ plots, clear titles.",
    heroEyebrow: "Our portfolio",
    heroLocations: "Mangaon · Raigad, Shrivardhan · Raigad, Dapoli · Ratnagiri, Shirdi · Ahilyanagar",
    heroImage: IMG.projectsHero,
    heroCaption: "Maharashtra · India",
    bandEyebrow: "Have a specific brief?",
    bandTitle: "Tell us what you're looking for.",
    bandLine: "Plot size, budget, sea-facing, near Shirdi - share your requirement and we'll match you to the right parcel across our four projects, including phases not yet listed on this page.",
    bandButton: "Share your brief",
  },

  // Home cinematic experience overlays.
  home: {
    // PLOT phase reveal title + master-plan hint card.
    plotRevealOverline: "Nausiddhi Realty presents",
    plotRevealTitle: "Nausiddhi",
    plotRevealSub: "A guided tour of the estate",
    plotHintLabel: "Master Plan",
    plotHintText: "Drag · Pinch to zoom · Tap for details",
    // MAP phase HUD. The 5 map pins themselves are fixed in MapPhase.jsx.
    mapHudNum: "02 / Setting",
    mapHudText: "Scroll to descend",
    // DAY AT NAUSIDDHI phase — the sunrise-to-night ground scene.
    dayScrollCue: "Scroll to begin the day",
    dayHeroLine1: "Every great journey begins",
    dayHeroLine2: "with a place that feels like",
    dayHeroEmphasis: "home.",
    dayCaptions: [
      "Live close to nature",
      "Designed for families",
      "Breathe fresh, every day",
    ],
    dayAmenities: [
      "The Clubhouse",
      "Open lawns & seating",
      "Meditation garden",
      "Reflecting waters",
    ],
    dayFinalLine1: "Not just a plot.",
    dayFinalEmphasis: "A place to belong.",
    dayFinalEyebrow: "Experience Nausiddhi",
    dayCtaText: "Reserve your dream today",
    // CONSTRUCTION phase — the build storyboard.
    constructionVideo: "", // optional video (currently a webp frame sequence)
    constructionLabel: "04 / Possibilities",
    // This storyboard narrates the accompanying timelapse (public/frames),
    // which shows a house being built on a finished Nausiddhi plot — bare
    // land → foundation marked → structure rising → landscaped home →
    // lit-up, move-in ready. Framed as "what your plot can become" rather
    // than Nausiddhi's own infrastructure work (that's already done by the
    // time you take possession — see the boundary wall / road / lighting
    // already visible in frame 1). Copy below is written to match what's
    // actually on screen at each stage; keep it that way if the video ever
    // changes. See STAGE_AT in ConstructionPhase.jsx for the frame timing.
    stages: [
      { label: "Your Plot", desc: "Cleared, walled, ready to build", h1: "This is where", h2: "it starts.", body: "A Nausiddhi plot arrives boundary-walled, road-connected and lit - blank canvas, zero groundwork left for you. What comes next is entirely yours to design.", statValue: "5,400+", statCaption: "Plots across 4 projects" },
      { label: "Foundation", desc: "Layout marked on site", h1: "Every home", h2: "starts on paper.", body: "The layout goes down first - walls, rooms and walkways marked directly on your plot before a single brick is laid.", statValue: "207 Acres", statCaption: "Nausiddhi Residence, Mangaon" },
      { label: "Structure", desc: "Walls and form take shape", h1: "Watch it", h2: "rise.", body: "Framework becomes form. Floor by floor, the outline on the ground becomes a house you can actually walk through.", statValue: "4", statCaption: "Active projects across Maharashtra" },
      { label: "Finished Home", desc: "Landscaped, move-in ready", h1: "From plot", h2: "to home.", body: "Landscaping, lighting and finish work complete the picture - proof of what a Nausiddhi plot can become when you're ready to build.", statValue: "1,350+", statCaption: "Happy families" },
      { label: "Yours", desc: "Registered, ready for you", h1: "Your land.", h2: "Your future.", body: "NA approved. Clear title. Registered in your name after possession. Four locations, one standard - Nausiddhi.", statValue: "NA", statCaption: "Non-agricultural approved" },
    ],
    // RESERVATION phase — closing CTA.
    resLabel: "05 / Reserve",
    resCount: 5200,
    resCountSub: "plots across 4 projects.",
    resBody: "Four active projects across Maharashtra - Mangaon, Shrivardhan, Dapoli, and Shirdi. NA approved, clear title, full infrastructure. Viewings open now.",
    resButton: "Enquire Now",
    resFooter: "Nausiddhi Realty Pvt. Ltd. · Pune, Maharashtra",
  },

  // ---------------------------------------------------------------------
  // NEW HOMEPAGE (video-hero layout). The legacy cinematic home still lives
  // at /experience and reads the `home.*` keys above; this block drives the
  // new default homepage only, so the two can be edited independently.
  // ---------------------------------------------------------------------
  homeV2: {
    metaTitle: "Nausiddhi Realty - NA-Approved Plotted Projects Across Maharashtra",
    metaDescription:
      "Premium NA-approved plotted developments in Mangaon, Shrivardhan, Dapoli and Shirdi. Clear freehold titles, complete infrastructure, registered possession.",

    // ---- HERO (full-screen video) ----
    // heroVideo: served from public/ by path — do NOT upload large video via
    // the admin panel (that stores a data-URL in localStorage, ~4MB cap).
    // Files in public/assets/ are referenced by root path like below.
    // heroPoster shows instantly while the video buffers and is what mobile
    // data-saver users see if autoplay is blocked.
    // Self-hosted hero loop, encoded from the "Project Overview | A Complete
    // Visual Walkthrough" film (youtu.be/DcpdhpY4XEw): 1600px, silent,
    // faststart, 6MB. Self-hosted deliberately — a YouTube embed re-requests
    // the file on every loop, which produced a black buffering frame AND
    // re-surfaced YouTube's player controls at each restart.
    heroVideo: "/assets/video/hero.mp4",
    heroYouTubeId: "",
    // Poster is frame 1 of that same file, so the still → video handoff is
    // seamless rather than a visible image swap.
    heroPoster: "/assets/video/hero-poster.jpg",
    heroEyebrow: "Nausiddhi Realty",
    heroTitle: "Land that holds",
    heroTitleEm: "its value.",
    heroSub:
      "NA-approved plotted developments across Maharashtra's growth corridors - clear title, complete infrastructure, registered in your name on day one.",
    heroPrimaryCta: "Explore Opportunities",
    heroSecondaryCta: "Book a Site Visit",
    heroScrollCue: "Scroll to explore",

    // ---- ABOUT (short intro band) ----
    aboutEyebrow: "Who we are",
    aboutTitle: "Built from Pune. Delivered across Maharashtra.",
    aboutBody:
      "Nausiddhi Realty develops plotted land in the corridors where Maharashtra is actually growing - led by decades of combined leadership expertise, and backed by a legal team that verifies every parcel before we acquire it.",
    aboutPoints: [
      "Clear, NA-approved title on every plot, verified before we buy it",
      "Roads, water, and electricity in place before you take possession—not just promised",
      "Registered in your name once possession is handed over",
    ],
    aboutCta: "More about us",
    aboutImage: IMG.homeAbout,

    // ---- STATS / COUNTERS ----
    statsEyebrow: "By the numbers",
    statsTitle: "Scale you can verify.",
    stats: [
      { value: 369, suffix: "+", label: "Acres under development" },
      { value: 5400, suffix: "+", label: "Plots across all projects" },
      { value: 1350, suffix: "+", label: "Happy families" },
      { value: 4, suffix: "+", label: "Active projects" },
    ],

    // ---- BUILD PROCESS ("How we build") ----
    buildEyebrow: "How we build",
    buildTitle: "Infrastructure before possession.",
    buildLede:
      "Every Nausiddhi plot is handed over ready to build on — walls, roads, water, power and landscaping completed before you take possession, not promised after. The five stages below are edited under Home (legacy).",

    // ---- PROJECTS OVERVIEW ----
    projectsEyebrow: "Our portfolio",
    projectsTitle: "Four locations. One standard.",
    projectsLede:
      "From the Mumbai-Goa corridor to the Konkan coast and Shirdi - each project is chosen for where infrastructure and demand are heading next.",
    projectsCta: "View all projects",

    // ---- TESTIMONIALS ----
    // PLACEHOLDER COPY — realistic but not real client quotes. Replace via
    // /admin -> Content -> Home (New) before launch.
    testimonialsEyebrow: "Client voices",
    testimonialsTitle: "What our buyers say.",
    testimonials: [
      {
        quote:
          "The title documents and NA approvals were shared before I paid anything. My lawyer verified everything independently. That transparency is why I bought a second plot.",
        name: "Rajesh Kulkarni",
        role: "Plot owner · Nausiddhi Residency, Mangaon",
      },
      {
        quote:
          "I'm based in Dubai, so I did the entire process remotely - video walkthrough, documents over email, registration through Power of Attorney. The team worked around my time zone throughout.",
        name: "Aditya Menon",
        role: "NRI buyer · BlueRain Resort Homes, Shrivardhan",
      },
      {
        quote:
          "What sold me was visiting the site and seeing the compound wall, roads and street lights already finished. Most developers show you a drawing. Here it was already built.",
        name: "Sunita Deshpande",
        role: "Plot owner · Sai Shrushti, Shirdi",
      },
    ],

    // ---- FAQ ----
    // Reviewed against client feedback — written to read as a real-estate
    // specialist's answers (named authorities, precise process/terminology)
    // rather than generic AI-sounding copy.
    faqEyebrow: "Good to know",
    faqTitle: "Questions, answered.",
    faqs: [
      {
        q: "What does 'NA-approved' actually mean?",
        a: "It means the land has official permission to be used for residential purposes, not farming - so you can legally build a home on it. Every Nausiddhi plot has this approval in place before we ever put it up for sale.",
      },
      {
        q: "When does the plot get registered in my name?",
        a: "Once we hand over possession. You get a proper registered sale deed in your name - not just a booking letter or an agreement - and your lawyer is welcome to review all the paperwork before that.",
      },
      {
        q: "Is the infrastructure finished before I take possession?",
        a: "Yes. Compound wall, internal roads, water supply, electricity and drainage are all completed before we hand your plot over - so it's ready to build on from day one, not something you wait on afterward.",
      },
      {
        q: "Can NRIs and out-of-state buyers purchase a plot?",
        a: "Yes, wherever you're based. We arrange a video walkthrough of the site, send you all the documents to review remotely, and can guide you through registering the plot without you needing to travel down in person.",
      },
      {
        q: "Are home loans available on these plots?",
        a: "Yes, most major banks offer plot loans, though approval depends on each bank's own criteria and their valuation of the plot. We'll share the documents your bank and advocate will ask for, and can point you to lenders our other buyers have used.",
      },
      {
        q: "Can I visit the site before deciding?",
        a: "Absolutely, just let us know in advance and we'll arrange a visit any day that works for you - we'll walk you through the plot, the infrastructure, and the overall layout in person. If you can't make it down, we'll do a live video walkthrough instead.",
      },
    ],

    // ---- CLOSING CTA ----
    ctaEyebrow: "Reserve your plot",
    ctaTitle: "See the land before you decide.",
    ctaBody:
      "Book a site visit - or a video walkthrough if you're outside India. We share title documents, approvals and the master plan before you commit to anything.",
    ctaPrimary: "Book a Site Visit",
    ctaSecondary: "Talk to our team",
    ctaImage: IMG.cta,
  },
};

// ---------------------------------------------------------------------------
// Persistence + deep merge (so new default keys appear after upgrades)
// ---------------------------------------------------------------------------
function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(base, override) {
  if (!isObj(base)) return override === undefined ? base : override;
  const out = Array.isArray(base) ? [...base] : { ...base };
  if (!isObj(override)) return out;
  for (const k of Object.keys(override)) {
    out[k] = isObj(base[k]) ? deepMerge(base[k], override[k]) : override[k];
  }
  return out;
}

let cache = null;

function load() {
  if (cache) return cache;
  let stored = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw);
  } catch {
    stored = null;
  }
  cache = stored ? deepMerge(DEFAULT_CONTENT, stored) : DEFAULT_CONTENT;
  return cache;
}

function persist(next) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("nr-content-change"));
  } catch {
    /* storage full/unavailable */
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function getContent() {
  return load();
}

// Read a value at a dotted path, e.g. get("settings.phone").
export function get(path, fallback = "") {
  const parts = path.split(".");
  let cur = load();
  for (const p of parts) {
    if (cur == null) return fallback;
    cur = cur[p];
  }
  return cur === undefined ? fallback : cur;
}

// Write a value at a dotted path and persist. Returns the new content.
export function set(path, value) {
  const parts = path.split(".");
  const root = deepMerge(load(), {}); // clone
  let cur = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    cur[k] = isObj(cur[k]) ? { ...cur[k] } : Array.isArray(cur[k]) ? [...cur[k]] : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  persist(root);
  return root;
}

// Replace the whole content object (used by import).
export function replaceAll(next) {
  persist(deepMerge(DEFAULT_CONTENT, next));
}

export function resetContent() {
  persist(DEFAULT_CONTENT);
}

export function subscribeContent(cb) {
  const local = () => cb();
  const cross = (e) => { if (e.key === KEY) { cache = null; cb(); } };
  window.addEventListener("nr-content-change", local);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener("nr-content-change", local);
    window.removeEventListener("storage", cross);
  };
}

// React hook: returns the whole content object and re-renders on any change.
export function useContent() {
  const [, force] = useState(0);
  useEffect(() => subscribeContent(() => force((n) => n + 1)), []);
  return getContent();
}

// Split a comma-separated field into a trimmed array.
export function commaList(text) {
  return String(text || "").split(",").map((s) => s.trim()).filter(Boolean);
}
